import {
    useQuery,
    UseQueryResult,
    UseMutationResult,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import {
    baselineTaskSearchResponseSchema,
    baselineTaskListFiltersResponseSchema,
    baselineTaskFormFiltersResponseSchema,
    baselineTaskSchema,
    ganttSearchResponseSchema,
    updateTaskDatesResponseSchema,
    BaselineTaskSearch,
    BaselineTaskSearchResponse,
    BaselineTaskListFiltersRequest,
    BaselineTaskListFiltersResponse,
    BaselineTaskFormFiltersRequest,
    BaselineTaskFormFiltersResponse,
    BaselineTask,
    BaselineTaskGanttSearch,
    GanttSearchResponse,
    UpdateTaskDatesRequest,
    UpdateTaskDatesResponse,
} from './baselineTask.types';
import { mutationResponseSchema, MutationResponse } from '../shared.types';
import { ActiveToggleResponse, activeToggleResponseSchema } from '../activeToggleForm/activeToggleForm.types';

// Re-export types for consumers
export type {
    BaselineTaskSearch,
    BaselineTaskSearchResponse,
    BaselineTaskListFiltersRequest,
    BaselineTaskListFiltersResponse,
    BaselineTaskFormFiltersRequest,
    BaselineTaskFormFiltersResponse,
    BaselineTask,
    BaselineTaskGanttSearch,
    GanttSearchResponse,
    GanttTask,
    UpdateTaskDatesRequest,
    UpdateTaskDatesResponse,
} from './baselineTask.types';

// Additional types for new endpoints  
export type UpdateContingentRequest = {
    taskId: number;
    contingentId: number | null;
};

export type OptionSearchRequest = {
    searchTerm: string;
    baselineId: number;
    baselineTaskId: number;
    isPrereqQuery: boolean;
};

export type OptionSearchResponse = {
    results: Array<{
        id: number;
        name: string;
    }>;
};

export type ActiveToggleRequest = {
    baselineTaskIds: number[];
    isActive: boolean;
    isLocationTaskListActive: boolean;
};

// Interface for raw API responses before sanitization
interface RawBaselineTaskSearchResponse {
    results?: any[];
    [key: string]: any;
}

interface RawBaselineTaskFiltersResponse {
    certifiedTaskId?: number | null | undefined;
    [key: string]: any;
}

interface RawBaselineTask {
    id?: number | null | undefined;
    certifiedTaskId?: number | null | undefined;
    [key: string]: any;
}

export const SEARCH_QUERY_KEY = 'BaselineTaskSearch';
export const GANTT_SEARCH_QUERY_KEY = 'BaselineTaskGanttSearch';
export const LIST_FILTERS_QUERY_KEY = 'BaselineTaskListFilters';
export const FORM_FILTERS_QUERY_KEY = 'BaselineTaskFormFilters';
export const GET_QUERY_KEY = 'BaselineTaskGet';

// ---- Search BaselineTasks ----

const searchBaselineTasks = async (searchRequest: BaselineTaskSearch): Promise<BaselineTaskSearchResponse> => {
    try {
        const response = await createPacerApi().post(`${API_ROOT}baselinetask/search`, { json: searchRequest }).json() as RawBaselineTaskSearchResponse;


        // Sanitize the response data to ensure all required fields are present for both mobile and desktop
        if (response && response.results && Array.isArray(response.results)) {
            response.results = response.results.map((task: RawBaselineTask) => {
                // Fill in all required fields with safe defaults
                return {
                    id: typeof task.id === 'number' ? task.id : 0,
                    name: typeof task.name === 'string' ? task.name : '',
                    isActive: typeof task.isActive === 'boolean' ? task.isActive : true,
                    isDeprecated: typeof task.isDeprecated === 'boolean' ? task.isDeprecated : false,
                    createdDate: task.createdDate ? task.createdDate : '',
                    modifiedDate: task.modifiedDate ? task.modifiedDate : '',
                    isChecked: typeof task.isChecked === 'boolean' ? task.isChecked : false,
                    startDaysOut: typeof task.startDaysOut === 'number' ? task.startDaysOut : null,
                    targetDaysOut: typeof task.targetDaysOut === 'number' ? task.targetDaysOut : 0,
                    startStaticDate: task.startStaticDate ? task.startStaticDate : '',
                    targetStaticDate: task.targetStaticDate ? task.targetStaticDate : '',
                    baselineJobTitle: typeof task.baselineJobTitle === 'string' ? task.baselineJobTitle : '',
                    additionalInfo: typeof task.additionalInfo === 'string' ? task.additionalInfo : '',
                    department: typeof task.department === 'string' ? task.department : '',
                    totalDependencies: typeof task.totalDependencies === 'number' ? task.totalDependencies : 0,
                    totalSubtasks: typeof task.totalSubtasks === 'number' ? task.totalSubtasks : 0,
                    totalResolvedFeedback: typeof task.totalResolvedFeedback === 'number' ? task.totalResolvedFeedback : 0,
                    isMilestone: typeof task.isMilestone === 'boolean' ? task.isMilestone : false,
                    contactName: typeof task.contactName === 'string' ? task.contactName : '',
                    contactEmail: typeof task.contactEmail === 'string' ? task.contactEmail : '',
                    links: Array.isArray(task.links) ? task.links : [],
                    discipline: typeof task.discipline === 'string' ? task.discipline : '',
                    community: typeof task.community === 'string' ? task.community : '',
                    phase: typeof task.phase === 'string' ? task.phase : '',
                    anticipatedEffort: typeof task.anticipatedEffort === 'string' ? task.anticipatedEffort : '',
                    tags: Array.isArray(task.tags) ? task.tags : [],
                    ordinal: typeof task.ordinal === 'number' ? task.ordinal : 0,
                    certifiedTaskId: task.certifiedTaskId === undefined ? null : task.certifiedTaskId,
                };
            });
        }

        const parsed = baselineTaskSearchResponseSchema.safeParse(response);
        if (!parsed.success) {
            // Never drop rows the API actually returned - schema drift used to surface as a
            // silently empty task list. Keep the sanitized payload and make the mismatch loud.
            console.error('searchBaselineTasks: response did not match schema, falling back to raw payload', parsed.error);
            return {
                results: Array.isArray(response?.results) ? response.results : [],
                totalCount: typeof response?.totalCount === 'number' ? response.totalCount : (Array.isArray(response?.results) ? response.results.length : 0),
                columnOrder: Array.isArray(response?.columnOrder) ? response.columnOrder : [],
            } as BaselineTaskSearchResponse;
        }
        return parsed.data;
    } catch (error) {
        console.error('searchBaselineTasks API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to search baseline tasks: ${error.message}`);
        }
        throw new Error('Failed to search baseline tasks: Unknown error');
    }
};

export const useSearchBaselineTasks = (
    searchRequest: BaselineTaskSearch,
    options?: { enabled?: boolean; refetchInterval?: number | false }
): UseQueryResult<BaselineTaskSearchResponse> => {
    return useQuery<BaselineTaskSearchResponse>({
        queryKey: [SEARCH_QUERY_KEY, searchRequest],
        queryFn: () => searchBaselineTasks(searchRequest),
        enabled: options?.enabled !== undefined ? options.enabled : true,
        refetchInterval: options?.refetchInterval ?? false,
    });
};

// ---- Load List Filters ----

const loadBaselineTaskListFilters = async (
    request: BaselineTaskListFiltersRequest
): Promise<BaselineTaskListFiltersResponse> => {
    try {
        const raw = await createPacerApi()
            .post(`${API_ROOT}baselinetask/list-filters`, { json: request })
            .json();
        // The server can return a double-encoded JSON string; normalize to object before parsing
        const response = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const parsed = baselineTaskListFiltersResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('loadBaselineTaskListFilters: response did not match schema, using fallback', parsed.error);
            return { departments: [], baselineJobTitles: [], communities: [], phases: [], disciplines: [], tags: [], isStartDateActive: true, isTaskStepsActive: true, isTaskEffortActive: true, isTaskFeedbackActive: true, isTaskDependenciesActive: true, isProjectWidgetActive: false, isWebhooksActive: false, isBaselineTaskValidationRulesActive: false };
        }
        return parsed.data;
    } catch (error) {
        console.error('loadBaselineTaskListFilters API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to load baseline task list filters: ${error.message}`);
        }
        throw new Error('Failed to load baseline task list filters: Unknown error');
    }
};

export const useLoadBaselineTaskListFilters = (
    request: BaselineTaskListFiltersRequest,
    options?: { enabled?: boolean }
): UseQueryResult<BaselineTaskListFiltersResponse> => {
    return useQuery<BaselineTaskListFiltersResponse>({
        queryKey: [LIST_FILTERS_QUERY_KEY, request],
        queryFn: () => loadBaselineTaskListFilters(request),
        enabled: options?.enabled !== undefined ? options.enabled : true,
    });
};

// ---- Load Form Filters ----

const loadBaselineTaskFormFilters = async (
    request: BaselineTaskFormFiltersRequest
): Promise<BaselineTaskFormFiltersResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/form-filters`, { json: request })
            .json() as RawBaselineTaskFiltersResponse;

        // Sanitize undefined number fields that cause Zod validation errors
        if (response) {
            if (response.certifiedTaskId === undefined) response.certifiedTaskId = null;
        }

        const parsed = baselineTaskFormFiltersResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('loadBaselineTaskFormFilters: response did not match schema, using fallback', parsed.error);
            return { departments: [], baselineJobTitles: [], communities: [], phases: [], taskTags: [], certifiedTaskId: null, subscriptionFiles: [] };
        }
        return parsed.data;
    } catch (error) {
        console.error('loadBaselineTaskFormFilters API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to load baseline task form filters: ${error.message}`);
        }
        throw new Error('Failed to load baseline task form filters: Unknown error');
    }
};

export const useLoadBaselineTaskFormFilters = (
    request: BaselineTaskFormFiltersRequest,
    options?: { enabled?: boolean }
): UseQueryResult<BaselineTaskFormFiltersResponse> => {
    return useQuery<BaselineTaskFormFiltersResponse>({
        queryKey: [FORM_FILTERS_QUERY_KEY, request],
        queryFn: () => loadBaselineTaskFormFilters(request),
        enabled: options?.enabled !== undefined ? options.enabled : true,
    });
};

// ---- Get Single BaselineTask ----

const getBaselineTask = async (id: number): Promise<BaselineTask> => {
    try {
        const response = await createPacerApi().get(`${API_ROOT}baselinetask/${id}`).json() as RawBaselineTask;

        // Sanitize undefined number fields
        if (response) {
            if (response.id === undefined) response.id = 0;
            if (response.certifiedTaskId === undefined) response.certifiedTaskId = null;
        }

        return baselineTaskSchema.parse(response);
    } catch (error) {
        console.error('getBaselineTask API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to get baseline task: ${error.message}`);
        }
        throw new Error('Failed to get baseline task: Unknown error');
    }
};

export const useGetBaselineTask = (
    id: number,
    options?: { enabled?: boolean }
): UseQueryResult<BaselineTask> => {
    return useQuery<BaselineTask>({
        queryKey: [GET_QUERY_KEY, id],
        queryFn: () => getBaselineTask(id),
        enabled: options?.enabled !== undefined ? options.enabled && id > 0 : id > 0,
    });
};

// ---- Save (Create / Update) BaselineTask ----

const saveBaselineTask = async (baselineTask: BaselineTask): Promise<MutationResponse> => {
    let response;
    if (baselineTask.id && baselineTask.id > 0) {
        // Existing baseline task - update
        response = await createPacerApi()
            .put(`${API_ROOT}baselinetask/update`, { json: baselineTask })
            .json();
    } else {
        // New baseline task - create
        response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/create`, { json: baselineTask })
            .json();
    }
    const parsed = mutationResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('saveBaselineTask: response did not match schema', parsed.error);
        return { id: 0, isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }] };
    }
    return parsed.data;
};

export const useSaveBaselineTask = (
    callback?: (results: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, BaselineTask> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, BaselineTask>({
        mutationFn: saveBaselineTask,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [GET_QUERY_KEY] });
        },
    });
};

// ---- Delete BaselineTask ----

const deleteBaselineTask = async (id: number): Promise<MutationResponse> => {
    const response = await createPacerApi().delete(`${API_ROOT}baselinetask/${id}`).json();
    const parsed = mutationResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('deleteBaselineTask: response did not match schema', parsed.error);
        return { id: 0, isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }] };
    }
    return parsed.data;
};

export const useDeleteBaselineTask = (
    callback?: (results: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, number> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, number>({
        mutationFn: deleteBaselineTask,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [GET_QUERY_KEY] });
        },
    });
};

// ---- Toggle Active BaselineTask ----

const toggleActiveBaselineTask = async (id: number): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .put(`${API_ROOT}baselinetask/toggle-active/${id}`)
        .json();
    const parsed = mutationResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('toggleActiveBaselineTask: response did not match schema', parsed.error);
        return { id: 0, isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }] };
    }
    return parsed.data;
};

export const useToggleActiveBaselineTask = (
    callback?: (results: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, number> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, number>({
        mutationFn: toggleActiveBaselineTask,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [GET_QUERY_KEY] });
        },
    });
};

// ---- Search BaselineTasks for Gantt ----

const searchBaselineTasksForGantt = async (searchRequest: BaselineTaskGanttSearch): Promise<GanttSearchResponse> => {
    try {
        const response = await createPacerApi().post(`${API_ROOT}baselinetask/search-gantt`, { json: searchRequest }).json();

        const parsed = ganttSearchResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('searchBaselineTasksForGantt: response did not match schema, using fallback', parsed.error);
            return { isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }], tasks: [], droppedTaskCount: 0 };
        }
        return parsed.data;
    } catch (error) {
        console.error('searchBaselineTasksForGantt API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to search baseline tasks for Gantt: ${error.message}`);
        }
        throw new Error('Failed to search baseline tasks for Gantt: Unknown error');
    }
};

export const useSearchBaselineTasksForGantt = (
    searchRequest: BaselineTaskGanttSearch,
    options?: { enabled?: boolean; refetchInterval?: number | false }
): UseQueryResult<GanttSearchResponse> => {
    return useQuery<GanttSearchResponse>({
        queryKey: [GANTT_SEARCH_QUERY_KEY, searchRequest],
        queryFn: () => searchBaselineTasksForGantt(searchRequest),
        enabled: options?.enabled !== undefined ? options.enabled : true,
        refetchInterval: options?.refetchInterval ?? false,
    });
};

// ---- Update Task Dates ----

const updateTaskDates = async (request: UpdateTaskDatesRequest): Promise<UpdateTaskDatesResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/update-dates`, { json: request })
            .json();
        const parsed = updateTaskDatesResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('updateTaskDates: response did not match schema, using fallback', parsed.error);
            return { isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }] };
        }
        return parsed.data;
    } catch (error) {
        console.error('updateTaskDates API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to update task dates: ${error.message}`);
        }
        throw new Error('Failed to update task dates: Unknown error');
    }
};

export const useUpdateTaskDates = (
    callback?: (results: UpdateTaskDatesResponse) => void
): UseMutationResult<UpdateTaskDatesResponse, Error, UpdateTaskDatesRequest> => {
    const queryClient = useQueryClient();
    return useMutation<UpdateTaskDatesResponse, Error, UpdateTaskDatesRequest>({
        mutationFn: updateTaskDates,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);

            if (!mutationResponse.isError) {
                queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
                queryClient.invalidateQueries({ queryKey: [GANTT_SEARCH_QUERY_KEY] });
            }
        },
    });
};

// ---- Update Contingent ----

const updateContingent = async (request: UpdateContingentRequest): Promise<MutationResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/update-contingent`, { json: request })
            .json();
        const parsed = mutationResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('updateContingent: response did not match schema', parsed.error);
            return { id: 0, isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }] };
        }
        return parsed.data;
    } catch (error) {
        console.error('updateContingent API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to update contingent: ${error.message}`);
        }
        throw new Error('Failed to update contingent: Unknown error');
    }
};

export const useUpdateContingent = (
    callback?: (results: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, UpdateContingentRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, UpdateContingentRequest>({
        mutationFn: updateContingent,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [GANTT_SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [GET_QUERY_KEY] });
        },
    });
};

// ---- Option Search ----

const optionSearchResponseSchema = z.object({
    results: z.array(z.object({
        id: z.number(),
        name: z.string(),
    })),
});

const optionSearch = async (request: OptionSearchRequest): Promise<OptionSearchResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/option-search`, { json: request })
            .json();
        const parsed = optionSearchResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('optionSearch: response did not match schema, using fallback', parsed.error);
            return { results: [] };
        }
        return parsed.data;
    } catch (error) {
        console.error('optionSearch API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to search options: ${error.message}`);
        }
        throw new Error('Failed to search options: Unknown error');
    }
};

export const useOptionSearch = (
    callback?: (results: OptionSearchResponse) => void
): UseMutationResult<OptionSearchResponse, Error, OptionSearchRequest> => {
    return useMutation<OptionSearchResponse, Error, OptionSearchRequest>({
        mutationFn: optionSearch,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
        },
    });
};

// ---- Active Toggle ----

const activeToggle = async (request: ActiveToggleRequest): Promise<ActiveToggleResponse> => {
    try {
        const response = await createPacerApi()
            .put(`${API_ROOT}baselinetask/active-toggle`, { json: request })
            .json();
        const parsed = activeToggleResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('activeToggle: response did not match schema', parsed.error);
            return { isError: true, errors: ['The server returned an unexpected response. Please try again.'] };
        }
        return parsed.data;
    } catch (error) {
        console.error('activeToggle API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to toggle active status: ${error.message}`);
        }
        throw new Error('Failed to toggle active status: Unknown error');
    }
};

export const useActiveToggle = (
    callback?: (results: ActiveToggleResponse) => void
): UseMutationResult<ActiveToggleResponse, Error, ActiveToggleRequest> => {
    const queryClient = useQueryClient();
    return useMutation<ActiveToggleResponse, Error, ActiveToggleRequest>({
        mutationFn: activeToggle,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [GANTT_SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [GET_QUERY_KEY] });
        },
    });
};